import './ModalContent.scss';

import clx from 'classnames';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { useResetCategoryAutoSelect } from '../../hooks/useResetCategoryAutoSelect';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { resetCarouselFormLoadedOnce, setCarouselViewOff } from '../../store/slices/carousel';
import { hideNavSelector } from '../../store/slices/global';
import { isWithdrrawalRejectOpened } from '../../store/slices/withdrawalReject';
import { isMobile } from '../../utils';
import { internalNavigateBack } from '../../utils/navigationControl';
import Header from '../components/Header/Header';
import { useSetHeaderConfig } from '../components/Header/hooks/useSetHeaderConfig';

type Props = {
  children: ReactNode;
  labelledById?: string;
};

export const ModalContent = ({ children, labelledById }: Props) => {
  const hideNav = useAppSelector(hideNavSelector);
  const isWithdrawalRejectOpened = useAppSelector(isWithdrrawalRejectOpened);
  const ref = useRef<HTMLDivElement>(null);
  const root = document.getElementById('root');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const resetCategoryAutoSelect = useResetCategoryAutoSelect();

  const handleCloseModal = () => {
    resetCategoryAutoSelect();
    dispatch(setCarouselViewOff());
    dispatch(resetCarouselFormLoadedOnce());
    internalNavigateBack(navigate);
  };

  useSetHeaderConfig({
    onCloseAction: handleCloseModal,
    onNavigationAction: handleCloseModal,
  });

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const focusable = node.querySelectorAll<HTMLElement>(
      'a,button,input,textarea,select,[tabindex]:not([tabindex="-1"])',
    );
    focusable[0]?.focus();
  }, []);

  // The modal covers the whole frame and scrolls internally; lock the document scroll so the
  // background page can't be scrolled from under it.
  useEffect(() => {
    document.documentElement.classList.add('app-no-scroll');
    return () => document.documentElement.classList.remove('app-no-scroll');
  }, []);

  return createPortal(
    <div
      id="modal-root"
      role="dialog"
      aria-modal="true"
      nonce={window.__webpack_nonce__}
      className={clx('modal-content-wrapper', {
        'no-header': hideNav || (isMobile && isWithdrawalRejectOpened),
      })}
      aria-labelledby={labelledById}
    >
      <div className="modal-content-header-wrapper">
        <Header />
      </div>
      <div className="modal-content-body-wrapper">
        <div ref={ref} className="modal-content-body">
          {children}
        </div>
      </div>
    </div>,
    root || document.body,
  ) as React.ReactElement;
};
